export const INVOICE_PERMISSION_GROUPS = [
  { id: "invoices", label: "Invoices" },
  { id: "catalog", label: "Catalog" },
  { id: "admin", label: "Administration" },
  { id: "settings", label: "Settings" },
] as const;

export type InvoicePermissionGroupId = (typeof INVOICE_PERMISSION_GROUPS)[number]["id"];

export const INVOICE_PERMISSIONS = [
  { key: "upload", label: "Upload", group: "invoices", path: "/upload" },
  { key: "dashboard", label: "Dashboard", group: "invoices", path: "/dashboard" },
  { key: "stores", label: "Stores", group: "catalog", path: "/stores" },
  { key: "suppliers", label: "Suppliers", group: "catalog", path: "/suppliers" },
  { key: "users", label: "Users & roles", group: "admin", path: "/users" },
  { key: "settings_sync", label: "Sync Settings", group: "settings", path: "/settings" },
] as const;

export type InvoicePermissionKey = (typeof INVOICE_PERMISSIONS)[number]["key"];

export const INVOICE_PERMISSION_KEYS = INVOICE_PERMISSIONS.map((p) => p.key);

export const STORE_ROLE_PERMISSIONS: InvoicePermissionKey[] = [
  "upload",
  "dashboard",
  "stores",
  "suppliers",
];

const PERMISSION_KEYS = new Set<string>(INVOICE_PERMISSION_KEYS);

type PermissionCarrier = {
  isSuperadmin?: boolean;
  permissions?: readonly string[] | string[];
};

export function isValidPermissionKey(key: string): key is InvoicePermissionKey {
  return PERMISSION_KEYS.has(key);
}

export function normalizePermissionKeys(keys: string[]): InvoicePermissionKey[] {
  const out: InvoicePermissionKey[] = [];
  const seen = new Set<string>();
  for (const raw of keys) {
    const k = raw.trim();
    if (!isValidPermissionKey(k) || seen.has(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out;
}

export function userHasPermission(user: PermissionCarrier, key: InvoicePermissionKey): boolean {
  if (user.isSuperadmin === true) return true;
  return Array.isArray(user.permissions) && user.permissions.includes(key);
}

export function invoicePermissionsForGroup(groupId: InvoicePermissionGroupId) {
  return INVOICE_PERMISSIONS.filter((p) => p.group === groupId);
}

export function pathAllowedForUser(user: PermissionCarrier, pathname: string): boolean {
  if (user.isSuperadmin === true) return true;
  if (pathname === "/review" || pathname.startsWith("/review/")) {
    return userHasPermission(user, "dashboard");
  }
  for (const p of INVOICE_PERMISSIONS) {
    if (pathname === p.path || pathname.startsWith(`${p.path}/`)) {
      return userHasPermission(user, p.key);
    }
  }
  return false;
}

export function defaultRedirectPathForUser(user: PermissionCarrier): string {
  if (user.isSuperadmin === true) return "/upload";
  for (const p of INVOICE_PERMISSIONS) {
    if (userHasPermission(user, p.key)) return p.path;
  }
  return "/upload";
}
